import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Popup from "reactjs-popup";
import client from "../../../shared/api/client";
import { searchSpotifyArtists } from "../../OfferEditUI/api/spotifyArtistApi";

const DEBOUNCE_MS = 300;
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);
const modalContentStyle = {
  width: "480px",
  maxWidth: "92vw",
  borderRadius: "8px",
  padding: 0,
  background: "var(--sl-bg-primary)",
  color: "var(--sl-text-primary)",
};
const dividerStyle = { borderColor: "var(--sl-border-secondary)" };

const EditArtistSpotify = ({ artistName, teamId, spotifyId, updateUrl }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(artistName);
  const [status, setStatus] = useState("idle"); // idle | loading | matches | empty
  const [matches, setMatches] = useState([]);
  const [selectedId, setSelectedId] = useState(spotifyId || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null); // [names] when the id is linked elsewhere
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  const runSearch = (q) => {
    const trimmed = q.trim();
    if (!trimmed) { setStatus("idle"); setMatches([]); return; }
    setStatus("loading");
    const myReq = ++reqRef.current;
    searchSpotifyArtists(teamId, trimmed)
      .then((rows) => {
        if (myReq !== reqRef.current) return;
        const list = Array.isArray(rows) ? rows : [];
        setMatches(list);
        setStatus(list.length ? "matches" : "empty");
      })
      .catch(() => { if (myReq === reqRef.current) { setMatches([]); setStatus("empty"); } });
  };

  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(name), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [name, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = () => {
    setName(artistName);
    setSelectedId(spotifyId || null);
    setError(null);
    setConflict(null);
    setOpen(true);
  };

  const pick = (id) => {
    setSelectedId(id);
    setError(null);
    setConflict(null);
  };

  const selected = matches.find((m) => m.spotify_id === selectedId) || null;
  const dupOther = selected && selected.linked_artist ? selected.linked_artist : null;
  const canSave = !saving && !!selectedId && !dupOther;
  const takeoverNames = conflict && conflict.length
    ? conflict.join(", ")
    : (dupOther ? dupOther.name : "another artist");

  const save = (takeover) => {
    if (saving || !selectedId) return;
    setSaving(true);
    setError(null);
    const payload = takeover ? { spotify_id: selectedId, takeover: true } : { spotify_id: selectedId };
    client.patch(updateUrl, payload)
      .then(() => window.location.reload())
      .catch((e) => {
        setSaving(false);
        const res = e && e.response;
        if (res && res.status === 409) {
          setConflict((res.data && res.data.conflict) || []);
        } else {
          setError((res && res.data && res.data[0]) || "Could not update the Spotify link.");
        }
      });
  };

  return (
    <React.Fragment>
      <button
        type="button"
        className="btn btn-link p-0 spotify-edit-btn"
        title="Edit Spotify"
        aria-label="Edit Spotify"
        onClick={openModal}
      >
        <i className="fa fa-pencil" />
      </button>

      <Popup open={open} modal closeOnDocumentClick contentStyle={modalContentStyle} onClose={() => setOpen(false)}>
        <div className="popup-modal-container" data-testid="edit-spotify-modal">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ padding: "16px", borderBottom: "2px solid", ...dividerStyle }}
          >
            <strong style={{ fontSize: "18px" }}>Link Spotify artist</strong>
            <a href="#" onClick={(e) => { e.preventDefault(); setOpen(false); }} style={{ fontSize: "22px", lineHeight: 1 }}>×</a>
          </div>

          <div style={{ padding: "16px" }}>
            <label className="d-block mb-1"><small className="text-muted">Search Spotify by artist name</small></label>
            <input
              type="text"
              className="form-control mb-3"
              data-testid="spotify-search-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />

            {status === "loading" && <div className="text-muted">Searching Spotify for “{name}”…</div>}
            {status === "empty" && <div className="text-muted">No Spotify artists found for “{name}”.</div>}

            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
              {status === "matches" && matches.map((m) => {
                const isSel = m.spotify_id === selectedId;
                return (
                  <button
                    key={m.spotify_id}
                    type="button"
                    data-testid="spotify-match"
                    className={`btn btn-block text-left d-flex align-items-center mb-2 ${isSel ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => pick(m.spotify_id)}
                    style={{ padding: "8px" }}
                  >
                    {m.image
                      ? <img src={m.image} alt="" width="40" height="40" style={{ borderRadius: "4px", objectFit: "cover" }} />
                      : <span style={{ width: 40, height: 40, borderRadius: 4, background: "#eee", display: "inline-block" }} />}
                    <span className="ml-2 d-flex flex-column">
                      <span className="font-weight-bold">{m.name}</span>
                      <span className="small">
                        {fmt(m.followers)} followers · {m.popularity} popularity
                        {m.linked_artist ? ` · linked to ${m.linked_artist.name}` : ""}
                      </span>
                    </span>
                    {isSel && <span className="ml-auto">✓</span>}
                  </button>
                );
              })}
            </div>

            {error && <div className="text-danger small mb-2" data-testid="spotify-error">{error}</div>}

            {(dupOther || conflict) && (
              <div className="alert alert-warning mb-0" data-testid="spotify-takeover">
                This Spotify profile is already linked to {takeoverNames}.
                Move it to this artist?
                <div className="mt-2 d-flex">
                  <button type="button" className="btn btn-sm btn-warning mr-2" data-testid="spotify-takeover-confirm" disabled={saving} onClick={() => save(true)}>
                    {saving ? "Moving…" : "Move it here"}
                  </button>
                  <button type="button" className="btn btn-sm btn-link" onClick={() => { setConflict(null); setSelectedId(null); }}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end" style={{ padding: "12px 16px", borderTop: "2px solid", ...dividerStyle }}>
            <button type="button" className="btn btn-link mr-2" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" data-testid="spotify-save" disabled={!canSave || !!conflict} onClick={() => save(false)}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

EditArtistSpotify.propTypes = {
  artistName: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  spotifyId: PropTypes.string,
  updateUrl: PropTypes.string.isRequired,
};

EditArtistSpotify.defaultProps = { spotifyId: null };

export default EditArtistSpotify;
